import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { SortModel, SubTableColumn } from 'src/components/commons/subTable/types';
import useInfinityDataSource, { FetchFunc } from 'src/hooks/useInfinityDataSource';
import PageInfo from 'src/modules/settings/modules/subTables/types';
import capitalizeFirstWord from 'src/utils/functions/capitalizeFirstWord';
import { AllFilters2, RegionModel } from 'src/graphql/schema-types';
import handleError from 'src/utils/functions/handleError';
import COLUMNS from '../constants';
import { GET_ALL_REGION_REGION, GET_REGION_HEADERS } from '../graphql/queries';
import { UPDATE_REGION_REGION, CREATE_REGION_REGION, REMOVE_REGION } from '../graphql/mutations';
import { GetAllRegions, GetAllRegionsResponse, GetRegionGridHeaders } from '../types';
import { filterIDAndAddingTitle } from '../../../../../utils/functions';

const useTabRegionCompanyTable = () => {
  const [getAll, queryResponse] = useLazyQuery<GetAllRegions>(GET_ALL_REGION_REGION, {
    fetchPolicy: 'network-only',
  });
  const [getAllSearch] = useLazyQuery<GetAllRegions>(GET_ALL_REGION_REGION, {
    fetchPolicy: 'network-only',
  });

  const [getHeaders] = useLazyQuery<GetRegionGridHeaders>(GET_REGION_HEADERS, {
    fetchPolicy: 'network-only',
  });
  const [update] = useMutation(UPDATE_REGION_REGION);
  const [create] = useMutation(CREATE_REGION_REGION);
  const [remove] = useMutation(REMOVE_REGION);

  const [pagination, setPagination] = useState<PageInfo>({ page: 1, pageSize: 1000 });
  const [columns, setColumns] = useState<SubTableColumn[]>([]);
  const [newRecord, setNewRecord] = useState({
    name: '',
  });

  useEffect(() => {
    getHeaders().then((res) => {
      const filteredResult = filterIDAndAddingTitle(
        res.data?.GetRegionGridHeaders as unknown as SubTableColumn[],
      )
        .filter((el) => el.gridVisible)
        .map((header) => {
          if (header.dataIndex === 'name')
            return {
              ...header,
              searchField: 'name',
            };
          return header;
        });
      setColumns(filteredResult ?? COLUMNS);
    });
  }, []);

  const fetchRegionPage: FetchFunc = ({ start, renderLen }) =>
    getAll({
      variables: {
        criteria: {
          pagination: {
            from: start,
            size: renderLen,
          },
        },
      },
    }).then((res) => ({
      results: res.data?.GetAllRegions2.results || [],
      total: res.data?.GetAllRegions2.total || 0,
    }));

  const { dataSource, onListRender, setDataSource, reset } = useInfinityDataSource(fetchRegionPage);

  const autocompleteFieldSearch = (
    searchField: string,
    value: string,
    dataType: string | number | string[],
  ) =>
    getAllSearch({
      variables: {
        criteria: {
          pagination: {
            from: 0,
            size: 100,
          },
          filter: [
            {
              fieldName: searchField,
              value,
              dataType,
              operator: AllFilters2.Contains,
            },
          ],
        },
        searchFields: {
          ids: null,
          names: null,
          enabled: null,
        },
      },
    }).then((res) => {
      if (res.data) {
        return {
          results: res?.data?.GetAllRegions2.results,
          filter: res?.data?.GetAllRegions2?.results.map((result: GetAllRegionsResponse) => ({
            value: result[searchField],
            label: result[searchField],
          })),
        };
      }

      return {
        results: [],
        filter: [],
      };
    });

  const resetNewRecord = () => {
    setNewRecord({
      name: '',
    });
  };

  const handleClearFilter = () => {
    reset();
  };

  const postNewRecord = async () => {
    if (newRecord.name === '') {
      return;
    }

    await create({
      variables: {
        createRegionInput: {
          name: capitalizeFirstWord(newRecord.name),
        },
      },
    }).then((region) => {
      setDataSource([
        { id: region.data.CreateRegion.id, name: region.data.CreateRegion.name },
        ...dataSource,
      ]);
    });
    resetNewRecord();
  };

  const handleUpdateRows = (rows: any[]) => {
    rows.forEach((row) => {
      const { id, name } = row;
      update({
        variables: {
          updateRegionInput: {
            id,
            name: capitalizeFirstWord(name),
          },
        },
      });
    });
  };

  const handleDeleteRows = async (rows: RegionModel[]) => {
    const removeId = rows[0].id;
    const messages = {
      400: {
        title: 'This record cannot be deleted',
        message: 'This value is linked to the company. You can not delete it.',
      },
    };

    try {
      await remove({ variables: { id: removeId } }).then(() => {
        setTimeout(() => {
          reset();
        }, 500);
      });
    } catch (error) {
      handleError(error, messages);
      console.log(error);
    }
  };

  const postData = (args: Record<string, unknown>) => {
    setNewRecord((prev) => ({
      ...prev,
      [args?.dataIndex as string]: args?.value as string,
      autoSave: !!args?.autoSave,
    }));
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
  };

  const handleFilterChange = (newFilters: Record<string, string[]>) => {
    setPagination({ ...pagination, filters: newFilters, page: 1 });
  };

  const handleSortModelChange = async (sortModel: SortModel | undefined) => {
    if (!sortModel) {
      // Load original data without any sorting
      try {
        const response = await getAll({
          variables: {
            criteria: {
              pagination: {
                from: (pagination.page - 1) * pagination.pageSize,
                size: pagination.pageSize,
              },
            },
          },
        });

        // Update data source with the original data
        if (response.data && response.data.GetAllRegions2) {
          setDataSource(response.data.GetAllRegions2.results);
        }
      } catch (error) {
        // Handle the error scenario
        console.error('Failed to fetch original data:', error);
      }
      return; // Early return to skip sorting if sortModel is undefined
    }

    // Check if sortBy is "parent" and change it to "parent_name"
    const sortByField = sortModel.sortBy === 'parent' ? 'parent_name' : sortModel.sortBy;

    try {
      // Update pagination with the new sort model
      setPagination((prev) => ({
        ...prev,
        sort: sortModel,
      }));

      // Fetch sorted data
      const response = await getAll({
        variables: {
          criteria: {
            pagination: {
              from: (pagination.page - 1) * pagination.pageSize,
              size: pagination.pageSize,
            },
            orderBy: [
              {
                field: sortByField,
                sort: sortModel.sort.toUpperCase(),
              },
            ],
          },
        },
      });

      // Update data source with new sorted data
      if (response.data && response.data.GetAllRegions2) {
        setDataSource(response.data.GetAllRegions2.results);
      }
    } catch (error) {
      // Handle the error scenario
      console.error('Failed to fetch sorted data:', error);
    }
  };

  return {
    autocompleteFieldSearch,
    handlePaginationChange,
    handleFilterChange,
    handleSortModelChange,
    pagination,
    columns,
    setColumns,
    dataSource,
    onListRender,
    handleUpdateRows,
    queryResponse,
    newRecord,
    postData,
    resetNewRecord,
    postNewRecord,
    handleClearFilter,
    handleDeleteRows,
  };
};

export default useTabRegionCompanyTable;
